<script setup lang="ts">
import { ref, onBeforeMount, reactive } from 'vue';
import i18n from '@/i18n';
import { DateTime } from 'luxon';
import { VC_TO_CREATE, VC_CREATED, VC_SENT, VC_EXPIRED, VC_REDEEMED, STATUS_OPEN, UNASSIGNED, CREATED, PACKAGE, HOTEL_ONLY, AIR_ONLY, CRUISE, TOUR } from '@/constants';
import { formatDate } from '@/utils/dateUtils';
import { replaceAll } from '@/utils/stringUtils';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useAdminQuoteStore } from '@/store/AdminQuoteStore';
import { useItemConfigurationStore } from '@/store/ItemConfigurationStore';
import { useViewQuoteStore } from '@/store/ViewQuoteStore';
import { useGroupTypeStore } from '@/store/GroupTypeStore';
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';
import { useDialog } from 'primevue/usedialog';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import DatePicker from 'primevue/datepicker';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import TableColumn from 'primevue/column';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import CreateVoucherModal from '@/components/modal/CreateVoucherModal.vue';
import FilterTable from '@/components/common/FilterTable.vue';
import RedeemVoucherModal from '@/components/modal/RedeemVoucherModal.vue';
import UrlQuoteModal from '@/components/modal/UrlQuoteModal.vue';

const voucher = ref([]);
const filters = ref();
const loading = ref(false);
const adminQuoteStore = useAdminQuoteStore();
const groupTypeManagement = useGroupTypeStore();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const pageSize = ref(10);
const currentFieldFilter = ref('');
const filterGlobalField = ref('');
const totalRecords = ref(null);
const isLoading = ref(false);
const dialog = useDialog();
const groupTypeList = ref([]);
const statuses = ref([VC_TO_CREATE, VC_CREATED, VC_SENT, VC_EXPIRED, VC_REDEEMED]);
const options = ref([{ name: i18n.global.t('group_type.wedding'), code: 'WE' }]);
const viewQuoteStore = useViewQuoteStore();
const configurationModule = useItemConfigurationStore();

const minDateRangeDefault = new Date(DateTime.utc().minus({ days: 60 }));
const maxDateRangeDefault = new Date();

const initValue = () => {
  return {
    typeOfSearch: CREATED,
    fromDate: minDateRangeDefault,
    toDate: maxDateRangeDefault,
    quoteType: [PACKAGE, HOTEL_ONLY, AIR_ONLY, CRUISE, TOUR],
    groupType: []
  };
};

const filterVoucherValue = reactive(initValue());

const initFilters = () => {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    coordinator: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    quoteId: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    groupName: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    passengerName: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    departDate: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
    voucherNumber: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    voucherSentDate: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
    voucherExpiry: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
    voucherStatus: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    reason: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }
  };
};
initFilters();

const getGroupTypeData = async () => {
  await groupTypeManagement.getGroupTypeList().then((response) => {
    groupTypeList.value = response.data;
    filterVoucherValue.groupType = getListGroupTypeID(response.data);
  });
};

onBeforeMount(async () => {
  await getGroupTypeData();
  await Promise.all([getListVoucher(), getActiveSignature()]);
  loading.value = false;
});

const getListGroupTypeID = (groupTypes) => {
  return groupTypes.map((item) => item?.sk);
};

const handleFilterData = (dataFilter) => {
  if (dataFilter) {
    filterVoucherValue.typeOfSearch = dataFilter.typeOfSearch;
    filterVoucherValue.fromDate = dataFilter.fromDate || initValue().fromDate;
    filterVoucherValue.toDate = dataFilter.toDate || initValue().toDate;
    filterVoucherValue.quoteType = dataFilter.quoteType;
    filterVoucherValue.groupType = dataFilter.groupType;
  } else {
    filterVoucherValue.typeOfSearch = initValue().typeOfSearch;
    filterVoucherValue.fromDate = initValue().fromDate;
    filterVoucherValue.toDate = initValue().toDate;
    filterVoucherValue.quoteType = initValue().quoteType;
    filterVoucherValue.groupType = getListGroupTypeID(groupTypeList.value);
  }
  getListVoucher();
};

const getListVoucher = async () => {
  const fromDate = new Date(filterVoucherValue.fromDate).setHours(0, 0, 0, 0);
  const toDate = new Date(filterVoucherValue.toDate).setHours(23, 59, 59, 0);
  const payload = {
    ...filterVoucherValue,
    fromDate: formatDate(fromDate, 'yyyy-LL-dd HH:mm', true).replace(' ', 'T'),
    toDate: formatDate(toDate, 'yyyy-LL-dd HH:mm', true).replace(' ', 'T')
  };
  isLoading.value = true;
  await adminQuoteStore.getListVoucher(payload).then((res) => {
    voucher.value = res.map((data, index) => {
      data.reason = data.groupType;
      data.departDate = data.departDate && new Date(data.departDate);
      data.voucherSentDate = data.voucherSentDate && new Date(data.voucherSentDate);
      data.voucherExpiry = data.voucherExpiry && new Date(data.voucherExpiry);
      data.voucherStatus = replaceAll(data.voucherStatus, '_', ' ');
      data.groupType = i18n.global.locale.value === 'en' ? data.groupType : data.groupTypeFr;
      data.coordinator = data.coordinator === UNASSIGNED ? i18n.global.t('status.unassigned') : data.coordinator;
      data.id = index;
      return data;
    });
    totalRecords.value = res.length;
    isLoading.value = false;
  });
};

const getActiveSignature = async () => {
  isLoading.value = true;
  await configurationModule.getActiveSignature(STATUS_OPEN).then(() => {
    isLoading.value = false;
  });
};

const handleFilterApply = (filterCallback, filterField) => {
  currentFieldFilter.value = filterField;
  filterCallback();
};

const handleClearFilter = (filterCallback, filterField) => {
  currentFieldFilter.value = filterField;
  filterCallback();
};

const clearFilter = () => {
  initFilters();
  filterGlobalField.value = '';
  getListVoucher();
};

const openCreateVoucher = (id, bookingNumber) => {
  const dialogRef = dialog.open(CreateVoucherModal, {
    data: {
      quoteId: id,
      bookingId: bookingNumber
    },
    props: {
      header: i18n.global.t('voucher.create_voucher'),
      modal: true,
      maximizable: true
    },
    emits: {
      isSave: () => {
        dialogRef.close();
        getListVoucher();
      }
    }
  });
};

const openRedeemVoucher = (id) => {
  const dialogRef = dialog.open(RedeemVoucherModal, {
    data: {
      quoteId: id
    },
    props: {
      header: i18n.global.t('voucher.redeem_voucher'),
      modal: true,
      maximizable: true
    },
    emits: {
      isSave: () => {
        dialogRef.close();
        getListVoucher();
      }
    }
  });
};

const openVoucher = (url) => {
  viewQuoteStore.urlQuote = url;
  dialog.open(UrlQuoteModal, {
    props: {
      header: i18n.global.t('config_items.voucher'),
      style: {
        width: '95vw'
      },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw'
      },
      modal: true
    }
  });
};
</script>

<template>
  <LoadingSpinner v-if="isLoading" />
  <div class="grid voucher-report">
    <div class="col-12">
      <div class="card" v-if="filters">
        <h2>{{ $t('voucher.title') }}</h2>
        <div class="filter-bar">
          <FilterTable :dataFilter="filterVoucherValue" :groupTypeList="groupTypeList" @handleFilterData="handleFilterData"></FilterTable>
        </div>
        <DataTable
          id="VoucherReport"
          :scrollable="true"
          :value="voucher"
          ref="dataTableRef"
          :paginator="true"
          class="p-datatable-gridlines"
          :rows="pageSize"
          dataKey="quoteId"
          v-model:filters="filters"
          :totalRecords="totalRecords"
          filterDisplay="menu"
          :loading="loading"
          responsiveLayout="stack"
          :globalFilterFields="['lang', 'quoteId', 'bookingNumber', 'groupName', 'coordinator', 'passengerName', 'voucherNumber', 'voucherStatus', 'reason', 'redeemBookingNo']"
          rowGroupMode="rowspan"
          groupRowsBy="quoteId"
        >
          <template #header>
            <div class="flex justify-content-between flex-col sm:flex-row">
              <div class="flex gap-2 align-items-center">
                <div class="flex justify-content-left flex-col sm:flex-row gap-3">
                  <Button type="button" id="clear-filter" icon="pi pi-filter-slash" :label="$t('common.buttons.clear')" class="p-button-secondary mb-2" @click="clearFilter()" />
                </div>
                <div>({{ $t('quote_management.hint_message') }} )</div>
              </div>
              <div class="justify-content-right">
                <span class="p-input-icon-left mb-2">
                  <i class="pi pi-search"></i>
                  <InputText v-model="filters['global'].value" :placeholder="$t('my_quote.search_keyword')" />
                </span>
              </div>
            </div>
          </template>
          <template #empty> {{ $t('my_quote.no_quotes_found') }} </template>
          <template #loading> {{ $t('common.generic.loading') }}. {{ $t('my_quote.please_wait') }}. </template>
          <TableColumn field="lang" header="Lang" sortable class="min-w-11rem">
            <template #body="{ data }">
              <div class="flex align-items-center gap-2">
                <span class="uppercase">{{ data.lang }}</span>
              </div>
            </template>
          </TableColumn>
          <TableColumn field="quoteId" sortable class="break-word min-w-11rem">
            <template #header>{{ $t('my_quote.quote_id') }}<br /><br />{{ $t('voucher.tableHeader.bookingId') }}</template>
            <template #body="{ data }">
              <span class="booking-number" v-tooltip="data.bookingNumber" :placeholder="$t('common.top')">{{ data.quoteId }}</span>
              <br />
              <span class="font-bold">{{ data.bookingNumber }}</span>
            </template>
          </TableColumn>

          <TableColumn :show-filter-operator="false" :show-add-button="false" field="coordinator" :header="$t('voucher.tableHeader.coordinator')" class="min-w-11rem" filter>
            <template #body="{ data }">
              <p>{{ data.coordinator }}</p>
            </template>
            <template #filter="{ filterModel }">
              <InputText type="text" v-model="filterModel.value" class="p-column-filter" :placeholder="$t('voucher.tableHeader.coorPlaceHolder')" />
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'coordinator')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'coordinator')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn :show-filter-operator="false" :show-add-button="false" field="groupName" class="min-w-11rem">
            <template #header
              >{{ $t('advance_quote.form.group_name') }}<br /><br />
              {{ $t('advance_quote.form.group_type') }}<br
            /></template>
            <template #body="{ data }">
              <p class="long-text-tooltip" v-tooltip="data.groupName" :placeholder="$t('common.top')">
                {{ data.groupName }}
              </p>
              <span>{{ data.groupType || '-' }}</span>
            </template>
            <template #filter="{ filterModel }">
              <InputText type="text" v-model="filterModel.value" class="p-column-filter" :placeholder="$t('voucher.tableHeader.groupNamePlaceHolder')" />
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'groupName')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'groupName')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn :show-filter-operator="false" :show-add-button="false" field="passengerName" :header="$t('voucher.tableHeader.passenger')" class="min-w-11rem">
            <template #body="{ data }">
              {{ data.passengerName }}
            </template>
            <template #filter="{ filterModel }">
              <InputText type="text" v-model="filterModel.value" class="p-column-filter" :placeholder="$t('voucher.tableHeader.searchByPassenger')" />
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'passengerName')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'passengerName')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn :show-filter-operator="false" :show-add-button="false" :header="$t('worksheet.departure_date')" filterField="departDate" dataType="date" class="min-w-11rem">
            <template #body="{ data }">
              {{ formatDate(data.departDate, 'dd-LLL-yyyy', false, i18n.global.locale.value) }}
            </template>
            <template #filter="{ filterModel }">
              <DatePicker v-model="filterModel.value" dateFormat="dd-M-yy" :placeholder="$t('common.date_format')">
                <template #footer>
                  <p class="text-center">
                    <small>{{ $t('common.press_space') }}</small>
                  </p>
                </template>
              </DatePicker>
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'departDate')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'departDate')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn :show-filter-operator="false" :show-add-button="false" field="voucherNumber" :header="$t('voucher.tableHeader.voucherNumber')" class="min-w-11rem" filter>
            <template #body="{ data }">
              {{ data.voucherNumber }}
            </template>
            <template #filter="{ filterModel }">
              <InputText type="text" v-model="filterModel.value" class="p-column-filter w-15rem" :placeholder="$t('voucher.tableHeader.searchByVoucherNumber')" />
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'voucherNumber')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'voucherNumber')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn :show-filter-operator="false" :show-add-button="false" field="voucherSentDate" :header="$t('voucher.tableHeader.voucherSentDate')" dataType="date" class="min-w-11rem" filter>
            <template #body="{ data }">
              {{ formatDate(data.voucherSentDate, 'dd-LLL-yyyy', false, i18n.global.locale.value) }}
            </template>
            <template #filter="{ filterModel }">
              <DatePicker v-model="filterModel.value" dateFormat="dd-M-yy" :placeholder="$t('common.date_format')">
                <template #footer>
                  <p class="text-center">
                    <small>{{ $t('common.press_space') }}</small>
                  </p>
                </template>
              </DatePicker>
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'voucherSentDate')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'voucherSentDate')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn :show-filter-operator="false" :show-add-button="false" field="voucherExpiry" :header="$t('voucher.tableHeader.voucherExpiry')" dataType="date" class="min-w-11rem" filter>
            <template #body="{ data }">
              {{ formatDate(data.voucherExpiry, 'dd-LLL-yyyy', false, i18n.global.locale.value) }}
            </template>
            <template #filter="{ filterModel }">
              <DatePicker v-model="filterModel.value" dateFormat="dd-M-yy" :placeholder="$t('common.date_format')">
                <template #footer>
                  <p class="text-center">
                    <small>{{ $t('common.press_space') }}</small>
                  </p>
                </template>
              </DatePicker>
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'voucherExpiry')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'voucherExpiry')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn :show-filter-operator="false" :show-add-button="false" field="voucherStatus" :header="$t('voucher.tableHeader.voucherStatus')" filterField="voucherStatus" class="min-w-11rem">
            <template #body="{ data }">
              <span class="font-bold text-sm">{{ $t(`voucher.${replaceAll(data.voucherStatus, ' ', '_').toLowerCase()}`) }}</span> <br />
              <span>{{ data.redeemBookingNo }}</span> <br />
              <span> {{ formatDate(data.redeemDate, 'dd-LLL-yyyy', false, i18n.global.locale.value) }}</span>
            </template>
            <template #filter="{ filterModel }">
              <Select v-model="filterModel.value" :options="statuses" :placeholder="$t('voucher.tableHeader.searchByVoucherStatus')" class="p-column-filter w-16rem" :showClear="true">
                <template #value="slotProps">
                  <span :class="'customer-badge'" v-if="slotProps.value">{{ $t(`voucher.${replaceAll(slotProps.value, ' ', '_').toLowerCase()}`) }}</span>
                  <span v-else>{{ slotProps.placeholder }}</span>
                </template>
                <template #option="slotProps">
                  <span :class="'customer-badge status-' + slotProps.option">{{ $t(`voucher.${replaceAll(slotProps.option, ' ', '_').toLowerCase()}`) }}</span>
                </template>
              </Select>
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'status')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'status')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn :show-filter-operator="false" :show-add-button="false" field="reason" :header="$t('voucher.tableHeader.reason')" class="min-w-11rem">
            <template #body="{ data }">
              <Select :options="options" optionLabel="name" optionValue="code" :placeholder="$t('voucher.tableHeader.choose_reason')" v-model="data.reason" class="input-reason" />
            </template>
            <template #filter="{ filterModel }">
              <Select v-model="filterModel.value" :options="options" option-value="code" optionLabel="name" :placeholder="$t('voucher.tableHeader.choose_reason')" class="w-full" />
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'reason')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'reason')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn :header="$t('my_quote.actions')" class="text-center min-w-11rem">
            <template #body="{ data }">
              <div class="grid">
                <div class="col-12">
                  <Button
                    id="open-voucher"
                    v-if="(data.voucherStatus === VC_SENT || data.voucherStatus === VC_REDEEMED || data.voucherStatus === VC_CREATED) && authorizations?.report?.voucher_report_action_view"
                    :label="$t('voucher.tableHeader.label-view')"
                    class="p-button-outlined p-button-xs border-noround w-full"
                    @click="openVoucher(data.urlPdf)"
                  />
                </div>
                <div class="col-12">
                  <Button
                    id="open-create-voucher"
                    v-if="(data.voucherStatus === VC_TO_CREATE || data.voucherStatus === VC_EXPIRED) && authorizations?.report?.voucher_report_create"
                    :label="$t('voucher.tableHeader.label-create')"
                    class="p-button-outlined p-button-xs border-noround w-full"
                    @click="openCreateVoucher(data.quoteId, data.bookingNumber)"
                  />
                </div>
                <div class="col-12">
                  <Button
                    id="open-redeem-voucher"
                    v-if="(data.voucherStatus === VC_CREATED || data.voucherStatus === VC_SENT) && authorizations?.report?.voucher_report_redeem"
                    :label="$t('voucher.tableHeader.label-redeem')"
                    class="p-button-outlined p-button-xs border-noround w-full"
                    @click="openRedeemVoucher(data.quoteId)"
                  />
                </div>
              </div>
            </template>
          </TableColumn>
        </DataTable>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
::v-deep(.booking-number) {
  vertical-align: middle;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}
::v-deep(.p-splitbutton.p-button-outlined > .p-button) {
  color: #00526e !important;
  border-radius: 0;
  padding: 2px;
}

:deep(.customer-badge.status-expired) {
  background: #ffe9eb !important;
}

::v-deep(.p-button-xs) {
  font-size: 0.775rem;
  padding: 0.25rem 0.5rem;
}

::v-deep(.p-datatable-table) {
  width: 100%;
}
</style>
